import { z } from 'zod';
import { passwordPolicy } from './schemas.js';

/* ───────────── papéis do Zentera Workspace (equipe do provedor) ───────────── */

export const WS_ROLES = ['owner', 'manager', 'attendant', 'viewer'] as const;
export type WsRole = (typeof WS_ROLES)[number];

export const WS_ROLE_LABELS: Record<WsRole, string> = {
  owner: 'Responsável',
  manager: 'Gestor',
  attendant: 'Atendente',
  viewer: 'Consulta',
};

export const WS_PERMISSIONS = [
  'knowledge.read',
  'knowledge.write',
  'settings.write',
  'team.read',
  'team.write',
  'audit.read',
  'assistant.test',
  'conversations.read',
  'dashboard.read',
] as const;
export type WsPermission = (typeof WS_PERMISSIONS)[number];

export const WS_ROLE_PERMISSIONS: Record<WsRole, readonly WsPermission[]> = {
  owner: WS_PERMISSIONS,
  manager: ['knowledge.read', 'knowledge.write', 'settings.write', 'team.read', 'audit.read', 'assistant.test', 'conversations.read', 'dashboard.read'],
  attendant: ['knowledge.read', 'conversations.read', 'dashboard.read'],
  viewer: ['knowledge.read', 'conversations.read', 'dashboard.read'],
};

export function wsCan(role: WsRole, p: WsPermission) {
  return WS_ROLE_PERMISSIONS[role].includes(p);
}

/* ───────────── conteúdo do provedor ───────────── */

const optionalText = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));

export const institutionSchema = z.object({
  tradeName: z.string().trim().min(2, 'Informe o nome').max(120),
  legalName: optionalText(200),
  supportPhone: optionalText(20),
  whatsappNumber: optionalText(20),
  email: z.string().trim().email('E-mail inválido').max(254).optional().or(z.literal('')),
  website: z.string().trim().url('URL inválida').max(200).optional().or(z.literal('')),
  address: optionalText(300),
  serviceArea: optionalText(1000),
  about: optionalText(3000),
});

export const planSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do plano').max(80),
  downloadMbps: z.coerce.number().int().min(1).max(100_000),
  uploadMbps: z.coerce.number().int().min(1).max(100_000),
  monthlyPriceCents: z.coerce.number().int().min(0).max(10_000_000),
  installationFeeCents: z.coerce.number().int().min(0).max(10_000_000).default(0),
  loyaltyMonths: z.coerce.number().int().min(0).max(60).default(0),
  description: optionalText(1000),
  active: z.boolean().default(true),
});
export type PlanInput = z.input<typeof planSchema>;

const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Formato HH:MM');
export const businessHoursSchema = z.object({
  days: z
    .array(
      z
        .object({ weekday: z.number().int().min(0).max(6), closed: z.boolean(), opens: time, closes: time })
        .refine((d) => d.closed || d.opens < d.closes, { message: 'Fechamento deve ser após a abertura', path: ['closes'] }),
    )
    .length(7),
  note: optionalText(500),
});

export const POLICY_KEYS = ['commercial', 'billing', 'installation', 'cancellation', 'support'] as const;
export type PolicyKey = (typeof POLICY_KEYS)[number];
export const POLICY_LABELS: Record<PolicyKey, { title: string; hint: string }> = {
  commercial: { title: 'Política comercial', hint: 'Condições de contratação, promoções vigentes, upgrade e downgrade de planos.' },
  billing: { title: 'Cobrança', hint: 'Vencimentos, formas de pagamento, multa e juros, prazos de suspensão e religação.' },
  installation: { title: 'Instalação', hint: 'Prazos, agendamento, equipamentos em comodato e requisitos do local.' },
  cancellation: { title: 'Cancelamento', hint: 'Como cancelar, fidelidade, devolução de equipamentos.' },
  support: { title: 'Suporte técnico', hint: 'Prazos de atendimento, visitas técnicas e procedimentos que o cliente pode fazer.' },
};
export const policySchema = z.object({ content: z.string().trim().max(20_000) });

export const faqSchema = z.object({
  question: z.string().trim().min(5, 'Pergunta muito curta').max(300),
  answer: z.string().trim().min(5, 'Resposta muito curta').max(5000),
  category: optionalText(60),
  published: z.boolean().default(true),
});

export const wsUserCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  role: z.enum(WS_ROLES),
  temporaryPassword: passwordPolicy,
});
export const wsUserUpdateSchema = z.object({ role: z.enum(WS_ROLES).optional(), disabled: z.boolean().optional() });
